import React, { useState, useEffect, useCallback } from 'react';
import WilayahBertingkat from '@/components/wilayah/WilayahBertingkat';
import { KOLOM_ALAMAT_GTK, alamatDariNilai, nilaiDariAlamat, ringkasAlamat } from '@/lib/wilayah';
import { nilaiAwalNama, susunNamaLengkap } from '@/lib/namaGelar';
import { bacaTanggal, masaKerjaGtk, selisihTahunBulan, teksTahunBulan } from '@/lib/masaWaktu';
import { ARSIP_BERKAS_GTK, ArsipBerkasGtk, BerkasGtk, DaftarRiwayatGtk, PilihJabatanGtk } from './gtk/RiwayatGtk';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  ArrowLeft, User, Briefcase, MapPin, Heart, Settings, Award,
  GraduationCap, FileText, Users, FolderOpen, Save, Edit, Upload, Download,
  Calendar, Phone, Mail, CreditCard, Home, CheckCircle, Clock, FileCheck, Plus
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/lib/AuthContext';
import { adalahTendik } from '@/lib/jenisGtk';
import { VervalDraftAlert, saveVervalDraft, clearVervalDraft, getVervalDraft } from '@/components/verval/VervalDraftAlert';

const formatTanggalId = (teks) => bacaTanggal(teks)?.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }) || teks;

/** Nilai hasil hitung otomatis (hanya baca) beserta keterangan sumbernya. */
function NilaiHitung({ nilai, kosong, keterangan, testid }) {
  return (
    <>
      <div className="mt-1 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm" data-testid={testid}>
        {nilai ? <span className="font-medium text-slate-800">{nilai}</span> : <span className="italic text-slate-400">{kosong}</span>}
      </div>
      {keterangan && <p className="mt-1 text-xs text-slate-500">{keterangan}</p>}
    </>
  );
}

export default function AdminGTKDetailPage({ userIdOverride = null, hideBackButton = false }) {
  const { id: paramId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const id = userIdOverride || paramId;
  const [gtk, setGtk] = useState(null);
  // Mode tendik mengikuti peran GTK yang dilihat (bukan peran pengguna yang login).
  const tendik = adalahTendik(gtk?.roles);
  const labelData = tendik ? 'Data Tendik' : 'Data Guru';
  const [vervalStatus, setVervalStatus] = useState({ hasDraft: false, hasPending: false, hasRejected: false });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('info-akun');
  const [activeSubTab, setActiveSubTab] = useState('data-diri');

  // Form states for different tabs
  const [formDataDiri, setFormDataDiri] = useState({});
  const [formKepegawaian, setFormKepegawaian] = useState({});
  const [formInformasiLain, setFormInformasiLain] = useState({});
  const [formTempatTinggal, setFormTempatTinggal] = useState({});
  const [formPerkawinan, setFormPerkawinan] = useState({});
  const [formPenugasan, setFormPenugasan] = useState({});

  // Status & Riwayat, daftar riwayat (pendidikan, diklat, penghargaan, anak, pesantren), dan arsip berkas
  const [formRiwayat, setFormRiwayat] = useState({});
  const setRiwayat = (field, nilai) => setFormRiwayat((f) => ({ ...f, [field]: nilai }));

  const loadGTKData = useCallback(async () => {
    setLoading(true);
    try {
      // Use /users/me/profile for self-profile (non-admin), /users/{id} for admin viewing others
      const endpoint = userIdOverride ? '/users/me/profile' : `/users/${id}`;
      const { data } = await api.get(endpoint);

      // Set all state in one go to prevent multiple re-renders
      setGtk(data);
      setFormDataDiri({
        full_name: data.full_name || '',
        ...nilaiAwalNama(data),
        gender: data.gender || '',
        birth_place: data.birth_place || '',
        birth_date: data.birth_date || '',
        nik: data.nik || '',
        nomor_kk: data.nomor_kk || '',
        nama_ibu_kandung: data.nama_ibu_kandung || '',
        agama: data.agama || '',
      });
      setFormKepegawaian({
        status_kepegawaian: data.status_kepegawaian || 'non_asn',
        nuptk: data.nuptk || data.nip_nuptk || '',
        nip: data.nip || '',
        peg_id: data.peg_id || '',
        npk: data.npk || '',
        nrg: data.nrg || '',
        tmt_pns: data.tmt_pns || '',
        no_sk_pns: data.no_sk_pns || '',
        tanggal_sk_pns: data.tanggal_sk_pns || '',
        file_sk_pns: data.file_sk_pns || '',
      });
      setFormInformasiLain({
        phone: data.phone || '',
        email: data.email || '',
        email_madrasah: data.email_madrasah || '',
        bpjs_kesehatan: data.bpjs_kesehatan || '',
        bpjs_ketenagakerjaan: data.bpjs_ketenagakerjaan || '',
        npwp: data.npwp || '',
        golongan_darah: data.golongan_darah || '',
        nama_rekening: data.nama_rekening || '',
        nomor_rekening: data.nomor_rekening || '',
        bank: data.bank || '',
      });
      setFormTempatTinggal({
        status_tempat_tinggal: data.status_tempat_tinggal || '',
        provinsi: data.provinsi || '',
        kode_wilayah: data.kode_wilayah || '',
        kab_kota: data.kab_kota || '',
        kecamatan: data.kecamatan || '',
        kelurahan: data.kelurahan || '',
        rt: data.rt || '',
        rw: data.rw || '',
        kode_pos: data.kode_pos || '',
        jarak_ke_sekolah: data.jarak_ke_sekolah || '',
        transportasi: data.transportasi || '',
        waktu_tempuh: data.waktu_tempuh || '',
        lintang: data.lintang || '',
        bujur: data.bujur || '',
      });
      setFormPerkawinan({
        status_perkawinan: data.status_perkawinan || 'belum_menikah',
        nama_pasangan: data.nama_pasangan || '',
      });
      setFormRiwayat({
        jabatan_ids: data.jabatan_ids || [],
        status_penugasan: data.status_penugasan || '',
        pangkat_golongan: data.pangkat_golongan || '',
        status_keaktifan: data.status_keaktifan || '',
        tanggal_pensiun: data.tanggal_pensiun || '',
        no_sk_pensiun: data.no_sk_pensiun || '',
        riwayat_pendidikan: data.riwayat_pendidikan || [],
        riwayat_diklat: data.riwayat_diklat || [],
        riwayat_penghargaan: data.riwayat_penghargaan || [],
        data_anak: data.data_anak || [],
        tidak_punya_anak: !!data.tidak_punya_anak,
        riwayat_pesantren: data.riwayat_pesantren || [],
        tidak_pernah_pesantren: !!data.tidak_pernah_pesantren,
        ...Object.fromEntries(ARSIP_BERKAS_GTK.map((b) => [b.field, data[b.field] || ''])),
      });
      setFormPenugasan({
        jenis_ptk: data.jenis_ptk || '',
        tmt_pegawai: data.tmt_pegawai || '',
        tmt_guru: data.tmt_guru || '',
        tugas_utama: data.tugas_utama || '',
        tugas_tambahan: data.tugas_tambahan || '',
      });

      // Load draft if exists (untuk self-profile non-admin)
      if (userIdOverride) {
        const draft = getVervalDraft(id);
        if (draft?.new_data) {
          // Apply draft data to forms
          const draftData = draft.new_data;
          if (draftData.full_name !== undefined) setFormDataDiri(prev => ({ ...prev, ...draftData }));
          if (draftData.status_kepegawaian !== undefined) setFormKepegawaian(prev => ({ ...prev, ...draftData }));
          if (draftData.phone !== undefined) setFormInformasiLain(prev => ({ ...prev, ...draftData }));
          if (draftData.status_tempat_tinggal !== undefined) setFormTempatTinggal(prev => ({ ...prev, ...draftData }));
          if (draftData.status_perkawinan !== undefined) setFormPerkawinan(prev => ({ ...prev, ...draftData }));
          if (draftData.jenis_ptk !== undefined) setFormPenugasan(prev => ({ ...prev, ...draftData }));
          setFormRiwayat(prev => ({ ...prev, ...Object.fromEntries(Object.keys(prev).filter((k) => draftData[k] !== undefined).map((k) => [k, draftData[k]])) }));
        }
      }

      setLoading(false);
    } catch (e) {
      toast.error('Gagal memuat data GTK');
      if (!userIdOverride) {
        navigate('/admin/gtk');
      }
    }
  }, [id, navigate, userIdOverride]);

  useEffect(() => {
    loadGTKData();
  }, [loadGTKData]);

  const handleSave = async () => {
    const payload = {
      ...formDataDiri,
      ...formKepegawaian,
      ...formInformasiLain,
      ...formTempatTinggal,
      ...formPerkawinan,
      ...formPenugasan,
      ...formRiwayat,
    };
    // Fungsi/Jabatan hanya bisa diubah admin (profil sendiri tidak boleh mengubah jabatan).
    if (!currentUser?.roles?.includes('admin') || userIdOverride) delete payload.jabatan_ids;

    const isAdmin = currentUser?.roles?.includes('admin');

    // Admin: langsung save ke database
    if (isAdmin && !userIdOverride) {
      try {
        await api.put(`/users/${id}`, payload);
        toast.success('Data berhasil disimpan');
        setEditing(false);
        loadGTKData();
      } catch (e) {
        toast.error('Gagal menyimpan data');
      }
      return;
    }

    // Non-admin (self-profile): save to draft verval
    // Check if there's already a pending request
    try {
      const { data: requests } = await api.get('/verval-requests');
      const hasPending = requests.some(req => req.status === 'pending' && req.user_id === id);

      if (hasPending) {
        toast.error('Masih ada ajuan verval yang pending. Batalkan atau tunggu hingga diproses terlebih dahulu.');
        return;
      }

      // Save to draft
      saveVervalDraft(id, payload);
      toast.success('Perubahan data disimpan sebagai draft. Klik "Ajukan Verval" untuk mengirim ke admin.');
      setEditing(false);
      loadGTKData(); // Reload to show draft alert
    } catch (e) {
      toast.error('Gagal menyimpan draft');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-slate-500">Memuat data...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          {!hideBackButton && (
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin/gtk')}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <div>
            <Badge className="bg-[#006837]/10 text-[#006837] border-[#006837]/20 mb-2">
              <User className="h-3 w-3 mr-1" /> Detail GTK
            </Badge>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" data-testid="judul-nama-gtk">
              {(editing ? susunNamaLengkap(formDataDiri) : (gtk?.nama_tanpa_gelar ? susunNamaLengkap(gtk) : '')) || gtk?.full_name}
            </h1>
            {(gtk?.gelar_depan || gtk?.gelar_belakang) && !editing && (
              <p className="mt-0.5 text-xs text-slate-500" data-testid="rincian-gelar-gtk">
                {gtk.gelar_depan && <>Gelar depan <span className="font-medium text-slate-700">{gtk.gelar_depan}</span></>}
                {gtk.gelar_depan && gtk.gelar_belakang && ' · '}
                {gtk.gelar_belakang && <>Gelar belakang <span className="font-medium text-slate-700">{gtk.gelar_belakang}</span></>}
              </p>
            )}
            <p className="text-sm text-slate-600 mt-1">
              {gtk?.nip || gtk?.nuptk || gtk?.nip_nuptk || 'Belum ada NIP/NUPTK'} • {gtk?.roles?.map(r => r.toUpperCase()).join(', ')}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {editing ? (
            <>
              <Button variant="outline" onClick={() => setEditing(false)}>Batal</Button>
              <Button onClick={handleSave} className="bg-[#006837] hover:bg-[#0B7A3B] gap-2">
                <Save className="h-4 w-4" /> Simpan
              </Button>
            </>
          ) : (
            <Button
              onClick={() => setEditing(true)}
              className="bg-[#006837] hover:bg-[#0B7A3B] gap-2"
              disabled={userIdOverride && vervalStatus.hasPending}
              title={userIdOverride && vervalStatus.hasPending ? 'Batalkan ajuan verval terlebih dahulu untuk edit data' : ''}
            >
              <Edit className="h-4 w-4" /> Edit Data
            </Button>
          )}
        </div>
      </div>

      {/* Verval Alert - Only show for self-profile (non-admin editing their own profile) */}
      {userIdOverride && (
        <VervalDraftAlert
          userId={id}
          userType={tendik ? 'tenaga_kependidikan' : 'guru'}
          onRefresh={loadGTKData}
          onStatusChange={setVervalStatus}
          onAdjustToDraft={(draft) => {
            // Load draft data to forms when user adjusts pending to draft
            if (draft?.new_data) {
              const draftData = draft.new_data;
              if (draftData.full_name !== undefined) setFormDataDiri(prev => ({ ...prev, ...draftData }));
              if (draftData.status_kepegawaian !== undefined) setFormKepegawaian(prev => ({ ...prev, ...draftData }));
              if (draftData.phone !== undefined) setFormInformasiLain(prev => ({ ...prev, ...draftData }));
              if (draftData.status_tempat_tinggal !== undefined) setFormTempatTinggal(prev => ({ ...prev, ...draftData }));
              if (draftData.status_perkawinan !== undefined) setFormPerkawinan(prev => ({ ...prev, ...draftData }));
              if (draftData.jenis_ptk !== undefined) setFormPenugasan(prev => ({ ...prev, ...draftData }));
          setFormRiwayat(prev => ({ ...prev, ...Object.fromEntries(Object.keys(prev).filter((k) => draftData[k] !== undefined).map((k) => [k, draftData[k]])) }));
            }
          }}
        />
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white border border-slate-200 w-full h-auto flex-wrap justify-start gap-1" data-testid="tabs-utama-gtk">
          <TabsTrigger value="info-akun" className="gap-2">
            <Settings className="h-4 w-4" /> Info Akun
          </TabsTrigger>
          <TabsTrigger value="data-guru" className="gap-2">
            <User className="h-4 w-4" /> {labelData}
          </TabsTrigger>
          <TabsTrigger value="status-kepegawaian" className="gap-2">
            <Briefcase className="h-4 w-4" /> Status & Riwayat
          </TabsTrigger>
          <TabsTrigger value="pendidikan" className="gap-2">
            <GraduationCap className="h-4 w-4" /> Pendidikan
          </TabsTrigger>
          <TabsTrigger value="diklat" className="gap-2">
            <Award className="h-4 w-4" /> Diklat/Pelatihan
          </TabsTrigger>
          <TabsTrigger value="penghargaan" className="gap-2">
            <Award className="h-4 w-4" /> Penghargaan
          </TabsTrigger>
          <TabsTrigger value="data-anak" className="gap-2">
            <Users className="h-4 w-4" /> Data Anak
          </TabsTrigger>
          <TabsTrigger value="riwayat-pesantren" className="gap-2">
            <FileText className="h-4 w-4" /> Riwayat Pesantren
          </TabsTrigger>
          <TabsTrigger value="arsip-berkas" className="gap-2">
            <FolderOpen className="h-4 w-4" /> Arsip Berkas
          </TabsTrigger>
        </TabsList>

        {/* TAB: INFO AKUN */}
        <TabsContent value="info-akun">
          <Card>
            <CardHeader>
              <CardTitle>Info Akun</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Nama Lengkap</Label>
                <Input value={(gtk?.nama_tanpa_gelar ? susunNamaLengkap(gtk) : '') || gtk?.full_name || ''} disabled />
                {gtk?.nama_tanpa_gelar && gtk.nama_tanpa_gelar !== gtk.full_name && (
                  <p className="mt-1 text-xs text-slate-500">Nama tanpa gelar: {gtk.nama_tanpa_gelar}</p>
                )}
              </div>
              <div>
                <Label>Username</Label>
                <Input value={gtk?.username || ''} disabled />
              </div>
              <div>
                <Label>Email</Label>
                <Input value={gtk?.email || ''} disabled />
              </div>
              <div>
                <Label>No. HP</Label>
                <Input value={gtk?.phone || ''} disabled />
              </div>
              <div>
                <Label>Role</Label>
                <Input value={(gtk?.roles || []).join(', ')} disabled />
              </div>
              <div>
                <Label>Status Akun</Label>
                <Input
                  value={gtk?.is_active === false ? 'Nonaktif' : 'Aktif'}
                  disabled
                />
              </div>
              <div>
                <Label>Status Password</Label>
                <Input value={gtk?.password_status || '-'} disabled />
              </div>
              <div>
                <Label>ID Pengguna</Label>
                <Input value={gtk?.id || ''} disabled />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB: DATA GURU / DATA TENDIK */}
        <TabsContent value="data-guru">
          <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
            <TabsList className="bg-slate-50 h-auto w-full flex-wrap justify-start gap-1" data-testid="tabs-data-gtk">
              <TabsTrigger value="data-diri">Formulir Data Diri</TabsTrigger>
              <TabsTrigger value="kepegawaian">Kepegawaian</TabsTrigger>
              <TabsTrigger value="informasi-lain">Informasi Lainnya</TabsTrigger>
              <TabsTrigger value="tempat-tinggal">Tempat Tinggal</TabsTrigger>
              <TabsTrigger value="perkawinan">Status Perkawinan</TabsTrigger>
              <TabsTrigger value="penugasan">Penugasan</TabsTrigger>
            </TabsList>

            {/* SUB-TAB: DATA DIRI */}
            <TabsContent value="data-diri">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Formulir Data Diri</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_minmax(0,1fr)] gap-3" data-testid="form-nama-gelar">
                    <div>
                      <Label>Gelar Depan</Label>
                      <Input value={formDataDiri.gelar_depan} placeholder="mis. Dr. / Drs. / H." disabled={!editing} data-testid="input-gelar-depan"
                        onChange={(e) => { const f = { ...formDataDiri, gelar_depan: e.target.value }; setFormDataDiri({ ...f, full_name: susunNamaLengkap(f) || f.full_name }); }} />
                    </div>
                    <div>
                      <Label>Nama Tanpa Gelar *</Label>
                      <Input value={formDataDiri.nama_tanpa_gelar} placeholder="mis. Ahmad Fauzi" disabled={!editing} data-testid="input-nama-tanpa-gelar"
                        onChange={(e) => { const f = { ...formDataDiri, nama_tanpa_gelar: e.target.value }; setFormDataDiri({ ...f, full_name: susunNamaLengkap(f) || f.full_name }); }} />
                    </div>
                    <div>
                      <Label>Gelar Belakang</Label>
                      <Input value={formDataDiri.gelar_belakang} placeholder="mis. S.Pd., M.Pd." disabled={!editing} data-testid="input-gelar-belakang"
                        onChange={(e) => { const f = { ...formDataDiri, gelar_belakang: e.target.value }; setFormDataDiri({ ...f, full_name: susunNamaLengkap(f) || f.full_name }); }} />
                    </div>
                    <div className="sm:col-span-3">
                      <Label>Nama Lengkap (otomatis)</Label>
                      <div className="mt-1 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-800" data-testid="nama-lengkap-otomatis">
                        {susunNamaLengkap(formDataDiri) || formDataDiri.full_name || <span className="italic font-normal text-slate-400">Isi nama tanpa gelar</span>}
                      </div>
                      <p className="mt-1 text-xs text-slate-500">Tersusun dari gelar depan + nama + gelar belakang; dipakai di seluruh aplikasi.</p>
                    </div>
                  </div>
                  <div>
                    <Label>Jenis Kelamin *</Label>
                    <Select value={formDataDiri.gender} onValueChange={(v) => setFormDataDiri({...formDataDiri, gender: v})} disabled={!editing}>
                      <SelectTrigger><SelectValue placeholder="Pilih..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="L">Laki-laki</SelectItem>
                        <SelectItem value="P">Perempuan</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Tempat Lahir</Label>
                    <Input value={formDataDiri.birth_place} onChange={(e) => setFormDataDiri({...formDataDiri, birth_place: e.target.value})} disabled={!editing} />
                  </div>
                  <div>
                    <Label>Tanggal Lahir</Label>
                    <Input type="date" value={formDataDiri.birth_date} onChange={(e) => setFormDataDiri({...formDataDiri, birth_date: e.target.value})} disabled={!editing} />
                  </div>
                  <div>
                    <Label>Umur Saat Ini</Label>
                    <NilaiHitung testid="umur-gtk"
                      nilai={teksTahunBulan(selisihTahunBulan(formDataDiri.birth_date))}
                      kosong={formDataDiri.birth_date ? 'Tanggal lahir tidak valid' : 'Isi tanggal lahir'}
                      keterangan="Dihitung otomatis dari tanggal lahir" />
                  </div>
                  <div>
                    <Label>Masa Kerja</Label>
                    {(() => {
                      const mk = masaKerjaGtk({ ...formKepegawaian, tmt_pegawai: formPenugasan.tmt_pegawai });
                      return (
                        <NilaiHitung testid="masa-kerja-gtk" nilai={teksTahunBulan(mk.lama)} kosong={mk.pesan}
                          keterangan={mk.sumber ? `Dihitung dari ${mk.sumber}${mk.tmt ? ` (${formatTanggalId(mk.tmt)})` : ''} — ${mk.sumber === 'TMT PNS' ? 'PNS/PPPK' : 'Non ASN'}` : 'Lengkapi status kepegawaian'} />
                      );
                    })()}
                  </div>
                  <div>
                    <Label>NIK</Label>
                    <Input value={formDataDiri.nik} onChange={(e) => setFormDataDiri({...formDataDiri, nik: e.target.value})} disabled={!editing} placeholder="16 digit" />
                  </div>
                  <div>
                    <Label>Nomor KK</Label>
                    <Input value={formDataDiri.nomor_kk} onChange={(e) => setFormDataDiri({...formDataDiri, nomor_kk: e.target.value})} disabled={!editing} placeholder="16 digit" />
                  </div>
                  <div>
                    <Label>Nama Ibu Kandung</Label>
                    <Input value={formDataDiri.nama_ibu_kandung} onChange={(e) => setFormDataDiri({...formDataDiri, nama_ibu_kandung: e.target.value})} disabled={!editing} />
                  </div>
                  <div>
                    <Label>Agama</Label>
                    <Select value={formDataDiri.agama} onValueChange={(v) => setFormDataDiri({...formDataDiri, agama: v})} disabled={!editing}>
                      <SelectTrigger><SelectValue placeholder="Pilih..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="islam">Islam</SelectItem>
                        <SelectItem value="kristen">Kristen</SelectItem>
                        <SelectItem value="katolik">Katolik</SelectItem>
                        <SelectItem value="hindu">Hindu</SelectItem>
                        <SelectItem value="buddha">Buddha</SelectItem>
                        <SelectItem value="konghucu">Konghucu</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* SUB-TAB: KEPEGAWAIAN */}
            <TabsContent value="kepegawaian">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Data Kepegawaian</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Status Kepegawaian *</Label>
                    <Select value={formKepegawaian.status_kepegawaian} onValueChange={(v) => setFormKepegawaian({...formKepegawaian, status_kepegawaian: v})} disabled={!editing}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pns">PNS</SelectItem>
                        <SelectItem value="pppk">PPPK</SelectItem>
                        <SelectItem value="non_asn">Non ASN</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Peg ID</Label>
                    <Input value={formKepegawaian.peg_id} onChange={(e) => setFormKepegawaian({...formKepegawaian, peg_id: e.target.value})} disabled={!editing} data-testid="input-peg-id" />
                  </div>
                  <div>
                    <Label>NUPTK</Label>
                    <Input value={formKepegawaian.nuptk} onChange={(e) => setFormKepegawaian({...formKepegawaian, nuptk: e.target.value})} disabled={!editing} />
                  </div>
                  <div>
                    <Label>NIP</Label>
                    <Input value={formKepegawaian.nip} onChange={(e) => setFormKepegawaian({...formKepegawaian, nip: e.target.value})} disabled={!editing} />
                  </div>
                  <div>
                    <Label>NPK</Label>
                    <Input value={formKepegawaian.npk} onChange={(e) => setFormKepegawaian({...formKepegawaian, npk: e.target.value})} disabled={!editing} data-testid="input-npk" />
                  </div>
                  {!tendik && (
                    <div>
                      <Label>NRG</Label>
                      <Input value={formKepegawaian.nrg} onChange={(e) => setFormKepegawaian({...formKepegawaian, nrg: e.target.value})} disabled={!editing} data-testid="input-nrg" />
                    </div>
                  )}
                  <div>
                    <Label>TMT PNS</Label>
                    <Input type="date" value={formKepegawaian.tmt_pns} onChange={(e) => setFormKepegawaian({...formKepegawaian, tmt_pns: e.target.value})} disabled={!editing} />
                  </div>
                  <div>
                    <Label>No SK PNS</Label>
                    <Input value={formKepegawaian.no_sk_pns} onChange={(e) => setFormKepegawaian({...formKepegawaian, no_sk_pns: e.target.value})} disabled={!editing} />
                  </div>
                  <div>
                    <Label>Tanggal SK</Label>
                    <Input type="date" value={formKepegawaian.tanggal_sk_pns} onChange={(e) => setFormKepegawaian({...formKepegawaian, tanggal_sk_pns: e.target.value})} disabled={!editing} />
                  </div>
                  <div>
                    <Label>File SK</Label>
                    <BerkasGtk gtkId={id} jenis="sk_pns" url={formKepegawaian.file_sk_pns} editing={editing} testid="berkas-sk-pns"
                      onChange={(url) => setFormKepegawaian({ ...formKepegawaian, file_sk_pns: url })} />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* SUB-TAB: INFORMASI LAINNYA */}
            <TabsContent value="informasi-lain">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Informasi Lainnya</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Nomor HP</Label>
                    <Input value={formInformasiLain.phone} onChange={(e) => setFormInformasiLain({...formInformasiLain, phone: e.target.value})} disabled={!editing} />
                  </div>
                  <div>
                    <Label>Email Pribadi</Label>
                    <Input type="email" value={formInformasiLain.email} onChange={(e) => setFormInformasiLain({...formInformasiLain, email: e.target.value})} disabled={!editing} />
                  </div>
                  <div>
                    <Label>Email Madrasah Hebat</Label>
                    <Input type="email" value={formInformasiLain.email_madrasah} onChange={(e) => setFormInformasiLain({...formInformasiLain, email_madrasah: e.target.value})} disabled={!editing} />
                  </div>
                  <div>
                    <Label>BPJS Kesehatan</Label>
                    <Input value={formInformasiLain.bpjs_kesehatan} onChange={(e) => setFormInformasiLain({...formInformasiLain, bpjs_kesehatan: e.target.value})} disabled={!editing} />
                  </div>
                  <div>
                    <Label>BPJS Ketenagakerjaan</Label>
                    <Input value={formInformasiLain.bpjs_ketenagakerjaan} onChange={(e) => setFormInformasiLain({...formInformasiLain, bpjs_ketenagakerjaan: e.target.value})} disabled={!editing} />
                  </div>
                  <div>
                    <Label>NPWP</Label>
                    <Input value={formInformasiLain.npwp} onChange={(e) => setFormInformasiLain({...formInformasiLain, npwp: e.target.value})} disabled={!editing} placeholder="15 digit" />
                  </div>
                  <div>
                    <Label>Golongan Darah</Label>
                    <Select value={formInformasiLain.golongan_darah} onValueChange={(v) => setFormInformasiLain({...formInformasiLain, golongan_darah: v})} disabled={!editing}>
                      <SelectTrigger><SelectValue placeholder="Pilih..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="A">A</SelectItem>
                        <SelectItem value="B">B</SelectItem>
                        <SelectItem value="AB">AB</SelectItem>
                        <SelectItem value="O">O</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Nama Pemilik Rekening</Label>
                    <Input value={formInformasiLain.nama_rekening} onChange={(e) => setFormInformasiLain({...formInformasiLain, nama_rekening: e.target.value})} disabled={!editing} />
                  </div>
                  <div>
                    <Label>Nomor Rekening</Label>
                    <Input value={formInformasiLain.nomor_rekening} onChange={(e) => setFormInformasiLain({...formInformasiLain, nomor_rekening: e.target.value})} disabled={!editing} />
                  </div>
                  <div>
                    <Label>Bank</Label>
                    <Input value={formInformasiLain.bank} onChange={(e) => setFormInformasiLain({...formInformasiLain, bank: e.target.value})} disabled={!editing} placeholder="BRI, BNI, Mandiri, dll" />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* SUB-TAB: TEMPAT TINGGAL */}
            <TabsContent value="tempat-tinggal">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Informasi Tempat Tinggal</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Status Tempat Tinggal</Label>
                    <Select value={formTempatTinggal.status_tempat_tinggal} onValueChange={(v) => setFormTempatTinggal({...formTempatTinggal, status_tempat_tinggal: v})} disabled={!editing}>
                      <SelectTrigger><SelectValue placeholder="Pilih..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="milik_sendiri">Milik Sendiri</SelectItem>
                        <SelectItem value="sewa">Sewa/Kontrak</SelectItem>
                        <SelectItem value="menumpang">Menumpang</SelectItem>
                        <SelectItem value="dinas">Rumah Dinas</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2" data-testid="gtk-wilayah-bertingkat">
                    <WilayahBertingkat
                      value={nilaiDariAlamat(formTempatTinggal, KOLOM_ALAMAT_GTK)}
                      onChange={(v, info) => setFormTempatTinggal({ ...formTempatTinggal, ...alamatDariNilai(v, info, KOLOM_ALAMAT_GTK) })}
                      disabled={!editing}
                      testidPrefix="gtk-wilayah"
                      ringkasan={ringkasAlamat({ ...formTempatTinggal, address: gtk?.address }, KOLOM_ALAMAT_GTK)}
                    />
                    {!formTempatTinggal.kode_wilayah && (formTempatTinggal.provinsi || formTempatTinggal.kab_kota) && (
                      <p className="mt-1 text-xs text-amber-700">
                        Alamat lama (belum dari master wilayah): {[formTempatTinggal.kelurahan, formTempatTinggal.kecamatan, formTempatTinggal.kab_kota, formTempatTinggal.provinsi].filter(Boolean).join(', ')} — pilih ulang dari daftar.
                      </p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>RT</Label>
                      <Input value={formTempatTinggal.rt} onChange={(e) => setFormTempatTinggal({...formTempatTinggal, rt: e.target.value})} disabled={!editing} />
                    </div>
                    <div>
                      <Label>RW</Label>
                      <Input value={formTempatTinggal.rw} onChange={(e) => setFormTempatTinggal({...formTempatTinggal, rw: e.target.value})} disabled={!editing} />
                    </div>
                  </div>
                  <div>
                    <Label>Kode Pos</Label>
                    <Input value={formTempatTinggal.kode_pos} onChange={(e) => setFormTempatTinggal({...formTempatTinggal, kode_pos: e.target.value})} disabled={!editing} />
                  </div>
                  <div>
                    <Label>Jarak ke Sekolah (km)</Label>
                    <Input type="number" value={formTempatTinggal.jarak_ke_sekolah} onChange={(e) => setFormTempatTinggal({...formTempatTinggal, jarak_ke_sekolah: e.target.value})} disabled={!editing} />
                  </div>
                  <div>
                    <Label>Transportasi ke Sekolah</Label>
                    <Select value={formTempatTinggal.transportasi} onValueChange={(v) => setFormTempatTinggal({...formTempatTinggal, transportasi: v})} disabled={!editing}>
                      <SelectTrigger><SelectValue placeholder="Pilih..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="jalan_kaki">Jalan Kaki</SelectItem>
                        <SelectItem value="sepeda">Sepeda</SelectItem>
                        <SelectItem value="motor">Sepeda Motor</SelectItem>
                        <SelectItem value="mobil">Mobil Pribadi</SelectItem>
                        <SelectItem value="angkutan_umum">Angkutan Umum</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Waktu Tempuh (menit)</Label>
                    <Input type="number" value={formTempatTinggal.waktu_tempuh} onChange={(e) => setFormTempatTinggal({...formTempatTinggal, waktu_tempuh: e.target.value})} disabled={!editing} />
                  </div>
                  <div>
                    <Label>Koordinat Lintang</Label>
                    <Input value={formTempatTinggal.lintang} onChange={(e) => setFormTempatTinggal({...formTempatTinggal, lintang: e.target.value})} disabled={!editing} placeholder="-6.200000" />
                  </div>
                  <div>
                    <Label>Koordinat Bujur</Label>
                    <Input value={formTempatTinggal.bujur} onChange={(e) => setFormTempatTinggal({...formTempatTinggal, bujur: e.target.value})} disabled={!editing} placeholder="106.816666" />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* SUB-TAB: STATUS PERKAWINAN */}
            <TabsContent value="perkawinan">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Status Perkawinan</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Status Perkawinan</Label>
                    <Select value={formPerkawinan.status_perkawinan} onValueChange={(v) => setFormPerkawinan({...formPerkawinan, status_perkawinan: v})} disabled={!editing}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="belum_menikah">Belum Menikah</SelectItem>
                        <SelectItem value="menikah">Menikah</SelectItem>
                        <SelectItem value="duda">Duda</SelectItem>
                        <SelectItem value="janda">Janda</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Nama Suami/Istri</Label>
                    <Input value={formPerkawinan.nama_pasangan} onChange={(e) => setFormPerkawinan({...formPerkawinan, nama_pasangan: e.target.value})} disabled={!editing || formPerkawinan.status_perkawinan === 'belum_menikah'} />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* SUB-TAB: PENUGASAN */}
            <TabsContent value="penugasan">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Data Penugasan</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Jenis PTK</Label>
                    <Select value={formPenugasan.jenis_ptk} onValueChange={(v) => setFormPenugasan({...formPenugasan, jenis_ptk: v})} disabled={!editing}>
                      <SelectTrigger><SelectValue placeholder="Pilih..." /></SelectTrigger>
                      <SelectContent>
                        {!tendik && <SelectItem value="guru_mapel">Guru Mata Pelajaran</SelectItem>}
                        {!tendik && <SelectItem value="guru_bk">Guru BK</SelectItem>}
                        {!tendik && <SelectItem value="kepala_sekolah">Kepala Sekolah</SelectItem>}
                        <SelectItem value="tenaga_administrasi">Tenaga Administrasi</SelectItem>
                        <SelectItem value="pustakawan">Pustakawan</SelectItem>
                        <SelectItem value="laboran">Laboran</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>TMT Pegawai</Label>
                    <Input type="date" value={formPenugasan.tmt_pegawai} onChange={(e) => setFormPenugasan({...formPenugasan, tmt_pegawai: e.target.value})} disabled={!editing} />
                  </div>
                  <div>
                    <Label>{tendik ? 'TMT/Tanggal SK PTK' : 'TMT Guru/Tanggal SK PTK'}</Label>
                    <Input type="date" value={formPenugasan.tmt_guru} onChange={(e) => setFormPenugasan({...formPenugasan, tmt_guru: e.target.value})} disabled={!editing} />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Tugas Utama</Label>
                    <Textarea value={formPenugasan.tugas_utama} onChange={(e) => setFormPenugasan({...formPenugasan, tugas_utama: e.target.value})} disabled={!editing} rows={2} />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Tugas Tambahan</Label>
                    <Textarea value={formPenugasan.tugas_tambahan} onChange={(e) => setFormPenugasan({...formPenugasan, tugas_tambahan: e.target.value})} disabled={!editing} rows={2} />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* TAB: STATUS DAN RIWAYAT KEPEGAWAIAN */}
        <TabsContent value="status-kepegawaian">
          <Card>
            <CardHeader>
              <CardTitle>Status dan Riwayat Kepegawaian</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>Fungsi/Jabatan</Label>
                  <PilihJabatanGtk value={formRiwayat.jabatan_ids || []} onChange={(v) => setRiwayat('jabatan_ids', v)}
                    disabled={!editing || !currentUser?.roles?.includes('admin') || !!userIdOverride} />
                  {editing && (!currentUser?.roles?.includes('admin') || userIdOverride) && <p className="mt-1 text-xs text-slate-500">Fungsi/jabatan diatur oleh admin.</p>}
                </div>
                <div>
                  <Label>Status Penugasan</Label>
                  <Select value={formRiwayat.status_penugasan || ''} onValueChange={(v) => setRiwayat('status_penugasan', v)} disabled={!editing}>
                    <SelectTrigger data-testid="select-status-penugasan"><SelectValue placeholder="Pilih..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="aktif">Aktif</SelectItem>
                      <SelectItem value="cuti">Cuti</SelectItem>
                      <SelectItem value="tugas_belajar">Tugas Belajar</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Pangkat/Golongan</Label>
                  <Input value={formRiwayat.pangkat_golongan || ''} onChange={(e) => setRiwayat('pangkat_golongan', e.target.value)}
                    placeholder="Contoh: Penata Muda, III/a" disabled={!editing} data-testid="input-pangkat-golongan" />
                </div>
                <div>
                  <Label>Status Keaktifan</Label>
                  <Select value={formRiwayat.status_keaktifan || ''} onValueChange={(v) => setRiwayat('status_keaktifan', v)} disabled={!editing}>
                    <SelectTrigger data-testid="select-status-keaktifan"><SelectValue placeholder="Pilih..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="aktif">Aktif</SelectItem>
                      <SelectItem value="non_aktif">Non Aktif</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold mb-2">Data Pensiun</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Tanggal Pensiun</Label>
                    <Input type="date" value={formRiwayat.tanggal_pensiun || ''} onChange={(e) => setRiwayat('tanggal_pensiun', e.target.value)} disabled={!editing} />
                  </div>
                  <div>
                    <Label>Nomor SK Pensiun</Label>
                    <Input value={formRiwayat.no_sk_pensiun || ''} onChange={(e) => setRiwayat('no_sk_pensiun', e.target.value)} disabled={!editing} />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pendidikan">
          <Card>
            <CardHeader><CardTitle>Riwayat Pendidikan Formal</CardTitle></CardHeader>
            <CardContent>
              <DaftarRiwayatGtk jenis="riwayat_pendidikan" items={formRiwayat.riwayat_pendidikan || []} editing={editing} testid="daftar-riwayat-pendidikan"
                onChange={(v) => setRiwayat('riwayat_pendidikan', v)} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="diklat">
          <Card>
            <CardHeader><CardTitle>Riwayat Diklat/Pelatihan</CardTitle></CardHeader>
            <CardContent>
              <DaftarRiwayatGtk jenis="riwayat_diklat" items={formRiwayat.riwayat_diklat || []} editing={editing} testid="daftar-riwayat-diklat"
                onChange={(v) => setRiwayat('riwayat_diklat', v)} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="penghargaan">
          <Card>
            <CardHeader><CardTitle>Riwayat Penghargaan</CardTitle></CardHeader>
            <CardContent>
              <DaftarRiwayatGtk jenis="riwayat_penghargaan" items={formRiwayat.riwayat_penghargaan || []} editing={editing} testid="daftar-riwayat-penghargaan"
                onChange={(v) => setRiwayat('riwayat_penghargaan', v)} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="data-anak">
          <Card>
            <CardHeader><CardTitle>Data Anak</CardTitle></CardHeader>
            <CardContent>
              <DaftarRiwayatGtk jenis="data_anak" items={formRiwayat.data_anak || []} editing={editing} testid="daftar-data-anak"
                onChange={(v) => setRiwayat('data_anak', v)}
                tidakAda={formRiwayat.tidak_punya_anak} onTidakAda={(v) => setRiwayat('tidak_punya_anak', v)} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="riwayat-pesantren">
          <Card>
            <CardHeader><CardTitle>Riwayat Pesantren</CardTitle></CardHeader>
            <CardContent>
              <DaftarRiwayatGtk jenis="riwayat_pesantren" items={formRiwayat.riwayat_pesantren || []} editing={editing} testid="daftar-riwayat-pesantren"
                onChange={(v) => setRiwayat('riwayat_pesantren', v)}
                tidakAda={formRiwayat.tidak_pernah_pesantren} onTidakAda={(v) => setRiwayat('tidak_pernah_pesantren', v)} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB: ARSIP BERKAS */}
        <TabsContent value="arsip-berkas">
          <Card>
            <CardHeader>
              <CardTitle>Arsip Berkas</CardTitle>
            </CardHeader>
            <CardContent>
              {!editing && <p className="mb-3 text-xs text-slate-500">Klik <b>Edit Data</b> untuk mengunggah berkas (PDF/JPG/PNG, maks. 2MB).</p>}
              <ArsipBerkasGtk gtkId={id} nilai={formRiwayat} editing={editing} onChange={(v) => setFormRiwayat(v)} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
